import {
  ConnectionQuality,
  MediaDeviceFailure,
  Room,
  RoomEvent,
} from 'livekit-client';

import { addWarningBreadcrumb, reportError } from '../../lib/sentry';

export const isWeak = (quality: ConnectionQuality) =>
  quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost;

// LiveKit emits MediaDevicesError with the same error object it then throws
// from setCameraEnabled/setMicrophoneEnabled, so remembering the object is
// exact where matching on getUserMedia's error names misses browser-specific
// ones such as Firefox's AbortError.
const deviceErrors = new WeakSet<Error>();

export const rememberDeviceErrors = (room: Pick<Room, 'on' | 'off'>) => {
  const remember = (error: Error) => {
    deviceErrors.add(error);
  };
  room.on(RoomEvent.MediaDevicesError, remember);
  return () => {
    room.off(RoomEvent.MediaDevicesError, remember);
  };
};

// DeviceFailedBanner shows these failures, so callers that also see the
// rejection should leave it to the banner.
export const isMediaDeviceError = (err: unknown): boolean =>
  err instanceof Error && deviceErrors.has(err);

// A blocked permission or a missing device is the clinician's own setup, not
// a fault in the app.
export const isExpectedDeviceFailure = (failure?: MediaDeviceFailure) =>
  failure === MediaDeviceFailure.PermissionDenied ||
  failure === MediaDeviceFailure.NotFound;

export const reportDeviceSwitchError = (err: unknown) => {
  // DeviceFailedBanner already reports a device the room flagged.
  if (isMediaDeviceError(err)) return;
  const failure =
    err instanceof Error ? MediaDeviceFailure.getFailure(err) : undefined;
  // Picking a device that another program, such as Teams, holds is also the
  // clinician's own setup. The menu tells them to close that program. Firefox
  // throws AbortError for a busy device, which getFailure maps to Other.
  const busy =
    failure === MediaDeviceFailure.DeviceInUse ||
    (err instanceof Error && err.name === 'AbortError');
  if (isExpectedDeviceFailure(failure) || busy) {
    addWarningBreadcrumb('video', `LiveKit device switch failed: ${failure}`);
    return;
  }
  reportError(err, { tags: { feature: 'video' } });
};
